import { useApolloClient } from '@apollo/react-hooks';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import _ from 'lodash';
import useTranslation from 'hooks/use-translation';
import useUser from 'hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { consumerLoginV2 } from 'src/state/actions/users';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import { getLogger } from 'src/utils/datadog-logger';
import { LOG_NAMESPACES } from 'src/payments/constants';
import { StatusTypes } from 'shared/constants/dutchie-pay';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

type UseSubmitPasswordResult = {
  submitPassword: ({ password }: { password: string }) => Promise<void>;
};

export const useSubmitPassword = (): UseSubmitPasswordResult => {
  const apolloClient = useApolloClient();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const showErnie = useErnie();

  const { t } = useTranslation();
  const User = useUser();
  const { isEmbedded } = useUI();

  const submitPassword = async ({ password, turnstileToken }): Promise<void> => {
    try {
      const result = await consumerLoginV2({
        apolloClient,
        User,
        email: DutchiePayEnrollment.userEmail,
        password,
        isEmbedded,
        turnstileToken,
        dispensaryId: DutchiePayEnrollment.dispensary?.id,
      });

      if (result.success) {
        const isEnrolled = User.payByBankEnrollmentStatus === StatusTypes.statusEnrolled;
        DutchiePayEnrollment.passwordStepCompleted(isEnrolled);
      } else {
        const message = _.get(
          result,
          `error.graphQLErrors[0].extensions.errors[0].detail`,
          t('login.fail', '{{message}}', { message: ERNIE_DEFAULT_MESSAGES.LOGIN })
        );

        logger.error('Password submission failed', {
          error: message ?? 'Unknown error',
          step: 'passwordForm',
          userId: User?.id ?? 'unknown',
          dispensaryId: DutchiePayEnrollment?.dispensary?.id ?? 'unknown',
        });

        showErnie(message, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
      }
    } catch (err) {
      logger.error('Password submission error', {
        error: err?.message ?? 'Unknown error',
        errorCode: err?.code ?? 'NO_CODE',
        step: 'passwordForm',
        userId: User?.id ?? 'unknown',
        dispensaryId: DutchiePayEnrollment?.dispensary?.id ?? 'unknown',
      });
      console.error(err);
      showErnie(err.message, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
    }
  };

  return {
    submitPassword,
  };
};
